import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  documentGetLinkedAction,
  documentLinkedOpenStatusSelector,
  documentLinkInListAction,
  documentSortedLinkedSelector,
  isLinkedDocsLockedSelector,
  linkedSelector,
  documentHideLinkedDocs,
  setLinkingTagAction,
} from 'domain/documents';
import { Set } from 'immutable';
import { documentWorkSpaceTypeSelector } from 'domain/companies';
import useToggle from 'hooks/useToggle';
import { promisify } from 'lib/helpers';

import { documentChangeLinkedWidthAction, linkedPanelWidthSelector, rtlEnable } from 'domain/env';

const mapStateToProps = (state) => ({
  linkedData: linkedSelector(state),
  linkedDocumentList: documentSortedLinkedSelector(state),
  panelWidth: linkedPanelWidthSelector(state),
  workspaceType: documentWorkSpaceTypeSelector(state),
  isOpen: documentLinkedOpenStatusSelector(state),
  isLocked: isLinkedDocsLockedSelector(state),
  isRtl: rtlEnable(state),
});

const PREVIEW_CONTEXT_NAME = 'LINKED_PANEL_PREVIEW';

const useLinkedPanel = ({
  clearSelection,
  selectedDocuments,
  onContextMenu,
  onPreview,
  onClick,
  openNotes,
  preview,
  getDocumentLinkedCount,
}) => {
  const dispatch = useDispatch();
  const [isOver, toggleIsOver] = useToggle();
  const { linkedDocumentList, isOpen, linkedData, isLocked, isRtl, panelWidth } = useSelector(mapStateToProps);
  const { tag, pageToken, count } = linkedData;
  const linkID = linkedDocumentList.getIn([0, 'linkid'], '');
  const note = linkedDocumentList.getIn([0, 'ltext'], '');
  const isPreviewOpen = preview.documentId && preview.contextName === PREVIEW_CONTEXT_NAME;

  const fetchNext = useCallback(() => {
    if (typeof pageToken === 'string') {
      const fn = (params) => dispatch(documentGetLinkedAction(params));

      promisify(fn, { pageToken, tag }).then();
    }
  }, [pageToken, tag, dispatch]);

  const handleContextMenu = useCallback(
    (...args) => {
      onContextMenu(...args, 'linked');
    },
    [onContextMenu],
  );

  const handleNoteClick = useCallback(
    (...args) => {
      openNotes(...args, 'linked');
    },
    [openNotes],
  );

  useEffect(() => {
    if (isOpen) getDocumentLinkedCount(linkID);
  }, [getDocumentLinkedCount, isOpen, linkID]);

  const handlePreviewClick = useCallback(
    (d) => {
      const [documentId, contextName] = d ? [d.documentID, PREVIEW_CONTEXT_NAME] : [null, ''];

      onPreview({ documentId, contextName });
    },
    [onPreview],
  );

  const handleDocumentClick = useCallback(
    (event, item) => {
      onClick(event, item, true);
    },
    [onClick],
  );

  const handleResize = useCallback(
    (width) => {
      window.localStorage.setItem('dokkaLinkedSidebarWidth', width);
      dispatch(documentChangeLinkedWidthAction(width));
    },
    [dispatch],
  );

  const onDndOver = (e) => {
    e.preventDefault();

    if (!isLocked && !isOver) {
      toggleIsOver();
    }
  };

  const onDndLeave = () => {
    if (isOver) {
      toggleIsOver();
    }
  };

  const onDndDrop = ({ linkid, id }) => {
    if (!linkedData.list.has(id) && !isLocked && !linkid) {
      const promise = new Promise((resolve, reject) => {
        dispatch(
          documentLinkInListAction({
            documentID: selectedDocuments.size ? selectedDocuments.toJS() : id,
            linkID,
            text: note,
            resolve,
            reject,
          }),
        );
      });

      promise.then(() => {
        getDocumentLinkedCount(linkID);
        if (tag) {
          clearSelection();
        }
      });
    }
    onDndLeave();
  };

  const onClose = useCallback(() => {
    dispatch(documentHideLinkedDocs());
    dispatch(setLinkingTagAction(''));
  }, [dispatch]);

  return {
    list: linkedDocumentList,
    total: count,
    linkID,
    note,
    panelWidth,
    isOpen,
    isOver,
    isPreviewOpen,
    isLocked,
    isRtl,
    onDndDrop,
    onDndOver,
    onDndLeave,
    onClose,
    handleNoteClick,
    handlePreviewClick,
    handleContextMenu,
    handleDocumentClick,
    handleResize,
    fetchNext,
  };
};

export default useLinkedPanel;
